import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, ChartLine } from '@phosphor-icons/react';
import LanguageSwitcher from '@/components/LanguageSwitcher';
import { openConsentPreferences } from '@/lib/consent';

const CONTENT = {
  pt: {
    title: 'Política de Privacidade',
    version: 'Versão 1.0 (rascunho) — última atualização: junho de 2026. Este documento é um rascunho técnico e carece de validação jurídica [PENDENTE DE PREENCHIMENTO/VALIDAÇÃO].',
    manage: 'Gerir cookies',
    back: 'Voltar',
    sections: [
      {
        h: '1. Responsável pelo tratamento',
        p: [
          'O serviço FunnelHack AI é operado por [PENDENTE DE PREENCHIMENTO/VALIDAÇÃO — nome legal da entidade], com sede em [PENDENTE DE PREENCHIMENTO/VALIDAÇÃO — morada].',
          'Contacto para questões de privacidade: [PENDENTE DE PREENCHIMENTO/VALIDAÇÃO — email de privacidade].',
        ],
      },
      {
        h: '2. Dados que recolhemos',
        p: [
          'Dados de conta: nome e email, fornecidos através da autenticação Google (Emergent Auth).',
          'Dados de pagamento: processados pela Stripe. Não guardamos números de cartão — apenas identificadores de transação e estado da subscrição.',
          'Dados de utilização do produto: funis analisados, conteúdos gerados por IA, monitorizações e configurações.',
          'Dados técnicos minimizados: endereço IP truncado (ex.: 192.168.1.0) e classe de dispositivo/navegador (ex.: "desktop/chrome") — nunca o IP completo nem o user-agent integral.',
          'Registo de consentimento: versão da política e categorias aceites, sem identificadores pessoais.',
        ],
      },
      {
        h: '3. Finalidades e bases legais',
        p: [
          'Execução do contrato: fornecer as funcionalidades do serviço (análise de funis, IA, monitorização).',
          'Consentimento: cookies e tracking de análise (métricas próprias de visitas, com IP truncado) e de marketing (atribuição UTM) — apenas quando aceites no banner de consentimento.',
          'Interesse legítimo: segurança, prevenção de abuso (rate limiting com IP truncado) e melhoria do serviço.',
          'Obrigação legal: faturação e registos contabilísticos.',
        ],
      },
      {
        h: '4. Subcontratantes e destinatários',
        p: [
          'Stripe (processamento de pagamentos), Resend (envio de emails transacionais), OpenAI (processamento de conteúdo pela IA) e o fornecedor de alojamento [PENDENTE DE PREENCHIMENTO/VALIDAÇÃO — lista completa de subcontratantes e respetivos acordos de tratamento de dados]. Não utilizamos ferramentas de análise de terceiros.',
        ],
      },
      {
        h: '5. Transferências internacionais',
        p: [
          'Alguns fornecedores operam fora do Espaço Económico Europeu (ex.: EUA). [PENDENTE DE PREENCHIMENTO/VALIDAÇÃO — mecanismos de transferência aplicáveis, como Cláusulas Contratuais-Tipo ou Data Privacy Framework].',
        ],
      },
      {
        h: '6. Prazos de conservação',
        p: [
          'Dados de conta: enquanto a conta estiver ativa. Após eliminação da conta, os dados associados são apagados.',
          'Dados de tracking minimizados: [PENDENTE DE PREENCHIMENTO/VALIDAÇÃO — prazo de conservação a definir].',
          'Dados de faturação: conservados pelo período exigido por lei.',
        ],
      },
      {
        h: '7. Os teus direitos',
        p: [
          'Tens direito de acesso, retificação, apagamento, portabilidade, limitação do tratamento, oposição e retirada do consentimento a qualquer momento.',
          'Podes exportar os teus dados e eliminar a tua conta diretamente em Definições, ou contactar-nos através de [PENDENTE DE PREENCHIMENTO/VALIDAÇÃO — email de privacidade].',
          'Tens ainda o direito de apresentar reclamação à autoridade de controlo — em Portugal, a CNPD (www.cnpd.pt).',
        ],
      },
      {
        h: '8. Alterações a esta política',
        p: [
          'Poderemos atualizar esta política. Alterações relevantes serão comunicadas na aplicação e a versão em vigor está sempre disponível nesta página.',
        ],
      },
    ],
  },
  en: {
    title: 'Privacy Policy',
    version: 'Version 1.0 (draft) — last updated: June 2026. This document is a technical draft pending legal validation [PENDENTE DE PREENCHIMENTO/VALIDAÇÃO].',
    manage: 'Manage cookies',
    back: 'Back',
    sections: [
      {
        h: '1. Data controller',
        p: [
          'The FunnelHack AI service is operated by [PENDENTE DE PREENCHIMENTO/VALIDAÇÃO — legal entity name], headquartered at [PENDENTE DE PREENCHIMENTO/VALIDAÇÃO — address].',
          'Privacy contact: [PENDENTE DE PREENCHIMENTO/VALIDAÇÃO — privacy email].',
        ],
      },
      {
        h: '2. Data we collect',
        p: [
          'Account data: name and email, provided through Google authentication (Emergent Auth).',
          'Payment data: processed by Stripe. We never store card numbers — only transaction identifiers and subscription status.',
          'Product usage data: analyzed funnels, AI-generated content, monitors and settings.',
          'Minimized technical data: truncated IP address (e.g. 192.168.1.0) and device/browser class (e.g. "desktop/chrome") — never the full IP or full user-agent.',
          'Consent log: policy version and accepted categories, without personal identifiers.',
        ],
      },
      {
        h: '3. Purposes and legal bases',
        p: [
          'Contract performance: providing the service features (funnel analysis, AI, monitoring).',
          'Consent: analytics cookies/tracking (first-party visit metrics, with truncated IP) and marketing (UTM attribution) — only when accepted in the consent banner.',
          'Legitimate interest: security, abuse prevention (rate limiting with truncated IP) and service improvement.',
          'Legal obligation: invoicing and accounting records.',
        ],
      },
      {
        h: '4. Processors and recipients',
        p: [
          'Stripe (payments), Resend (transactional emails), OpenAI (AI content processing) and the hosting provider [PENDENTE DE PREENCHIMENTO/VALIDAÇÃO — full processor list and data processing agreements]. We do not use third-party analytics tools.',
        ],
      },
      {
        h: '5. International transfers',
        p: [
          'Some providers operate outside the European Economic Area (e.g. USA). [PENDENTE DE PREENCHIMENTO/VALIDAÇÃO — applicable transfer mechanisms such as Standard Contractual Clauses or the Data Privacy Framework].',
        ],
      },
      {
        h: '6. Retention periods',
        p: [
          'Account data: for as long as the account is active. After account deletion, associated data is erased.',
          'Minimized tracking data: [PENDENTE DE PREENCHIMENTO/VALIDAÇÃO — retention period to be defined].',
          'Billing data: retained for the legally required period.',
        ],
      },
      {
        h: '7. Your rights',
        p: [
          'You have the right of access, rectification, erasure, portability, restriction of processing, objection, and to withdraw consent at any time.',
          'You can export your data and delete your account directly in Settings, or contact us at [PENDENTE DE PREENCHIMENTO/VALIDAÇÃO — privacy email].',
          'You also have the right to lodge a complaint with a supervisory authority — in Portugal, the CNPD (www.cnpd.pt).',
        ],
      },
      {
        h: '8. Changes to this policy',
        p: [
          'We may update this policy. Material changes will be announced in the app and the current version is always available on this page.',
        ],
      },
    ],
  },
};

export default function PrivacyPolicy() {
  const { i18n } = useTranslation();
  const lang = (i18n.resolvedLanguage || i18n.language || 'en').slice(0, 2);
  const c = CONTENT[lang] || CONTENT.en;

  return (
    <div data-testid="privacy-policy-page" className="min-h-screen bg-[#050505] text-white px-6 py-10">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <Link to="/" data-testid="policy-back-link" className="flex items-center gap-2 text-white/60 hover:text-white transition text-sm">
            <ArrowLeft size={16} /> {c.back}
          </Link>
          <LanguageSwitcher variant="compact" />
        </div>
        <div className="flex items-center gap-2 mb-6">
          <div className="w-8 h-8 bg-gradient-to-br from-[#2563EB] to-[#3B82F6] rounded-lg flex items-center justify-center">
            <ChartLine size={18} weight="bold" className="text-white" />
          </div>
          <span className="text-white/80 font-[Outfit] font-bold">FunnelHack AI</span>
        </div>
        <h1 className="text-3xl sm:text-4xl font-bold font-[Outfit] mb-3">{c.title}</h1>
        <p className="text-xs text-amber-400/90 bg-amber-400/10 border border-amber-400/20 rounded-lg px-3 py-2 mb-8">{c.version}</p>
        {c.sections.map((s) => (
          <section key={s.h} className="mb-7">
            <h2 className="text-lg font-semibold font-[Outfit] mb-2">{s.h}</h2>
            {s.p.map((para, i) => (
              <p key={i} className="text-sm text-white/60 leading-relaxed mb-2">{para}</p>
            ))}
          </section>
        ))}
        <button
          data-testid="manage-cookies-btn"
          onClick={openConsentPreferences}
          className="mt-4 px-5 py-2.5 rounded-full border border-white/25 text-sm text-white/80 hover:text-white hover:border-white/50 transition"
        >
          {c.manage}
        </button>
      </div>
    </div>
  );
}
